import { WidgetType } from 'types/widget.types';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { MapWidgetData } from 'widgets/MapWidget/MapWidget.types';

/**
 * Map of widget type to widget key
 */
const widgetTypeToKeyMap = Object.entries(WidgetType).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {} as Record<WidgetType, string>);

/**
 * Check if a value is non-empty
 */
const isNonEmpty = (v: number | string | null | undefined) => v !== '' && v !== null && v !== undefined;

/**
 * Fill empty value with empty string
 * Note: zero is not considered empty
 */
const fillWithEmptyString = (v: number | string | null | undefined) => (isNonEmpty(v) ? String(v) : '');

/**
 * Transpose the matrix and fill all empty value with empty string
 *
 * @param matrix
 * @returns
 */
const transposeAndFill = (matrix: string[][]) =>
  matrix[0].map((_, colIndex) => matrix.map((row) => fillWithEmptyString(row[colIndex])));

/**
 * Get data unit from number format
 */
const getDataUnit = (numberFormat: ChartWidgetData['numberFormat'], dataLabels?: ChartWidgetData['dataLabels']) => {
  switch (numberFormat?.format) {
    case 'percentage':
      return '%';
    default:
      const { suffix } = dataLabels || {};
      return suffix ?? '';
  }
};

/**
 * Get prefix and suffix from data labels
 *
 * @param dataLabels
 * @returns
 */
const getFormatValue = (dataLabels: MapWidgetData['series']['dataLabels'] | ChartWidgetData['dataLabels']) => {
  const { prefix, suffix } = dataLabels || {};

  return {
    prefix: prefix ?? '',
    suffix: suffix ?? '',
  };
};

/**
 * Normalize and convert series data to tabular data
 *
 * @param seriesData
 * @returns tabular data
 */
const seriesDataToTabularData = (seriesData?: ChartWidgetData['seriesData']) => {
  const records: string[][] = [];
  const header: Array<string> = [];

  if (!Array.isArray(seriesData)) {
    return undefined;
  }

  /**
   * Iterate through each series and trim the trailing rows and columns that have empty header
   */
  for (const series of seriesData) {
    if (Array.isArray(series.data) && series.data.length > 0) {
      let lastNoNEmptyIndex = 0;

      for (let i = series.data.length - 1; i > 0; i--) {
        if (isNonEmpty(series.data[i])) {
          lastNoNEmptyIndex = i;
          break;
        }
      }

      if (lastNoNEmptyIndex > 0) {
        records.push(series.data.slice(0, lastNoNEmptyIndex + 1).map((v) => fillWithEmptyString(v)));
      }

      header.push(fillWithEmptyString(series.name));
    }
  }

  if (records.length > 0 && header.length > 0) {
    const valueRecords = transposeAndFill(records);
    return [header.slice(0, valueRecords[0].length), ...valueRecords];
  }
};

const singleSeriesDataToTabularData = (seriesData?: ChartWidgetData['seriesData']) => {
  const series = seriesData?.[0]?.data;

  if (!Array.isArray(series)) {
    return undefined;
  }

  let firstNonEmptyIndex = 0;
  let lastNonEmptyIndex = series.length - 1;

  // Trim leading and trailing empty values
  while (firstNonEmptyIndex <= lastNonEmptyIndex) {
    const isFirstNonEmpty = isNonEmpty(series[firstNonEmptyIndex]);
    const isLastNonEmpty = isNonEmpty(series[lastNonEmptyIndex]);

    if (isFirstNonEmpty && isLastNonEmpty) break;
    if (!isFirstNonEmpty) firstNonEmptyIndex++;
    if (!isLastNonEmpty) lastNonEmptyIndex--;
  }

  return series.slice(firstNonEmptyIndex, lastNonEmptyIndex + 1).map((v) => fillWithEmptyString(v));
};

const mapSeriesDataToTabularData = (seriesData?: MapWidgetData['seriesData']) => {
  if (!Array.isArray(seriesData)) {
    return undefined;
  }

  const names = seriesData[0]?.data;
  const values = seriesData[1]?.data;

  if (!Array.isArray(names) || !Array.isArray(values)) {
    return undefined;
  }

  return names
    .map((name, index) => {
      if (!isNonEmpty(name)) return undefined;
      return [fillWithEmptyString(name), fillWithEmptyString(values[index])];
    })
    .filter(Array.isArray);
};

/**
 * Hash a message with SHA-256
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
 * @param message
 * @returns
 */
const digestMessage = async (message: string) => {
  if (!crypto?.subtle) {
    return undefined;
  }

  const utf8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export {
  widgetTypeToKeyMap,
  transposeAndFill,
  getDataUnit,
  getFormatValue,
  singleSeriesDataToTabularData,
  seriesDataToTabularData,
  mapSeriesDataToTabularData,
  digestMessage,
};
